import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { catchError, finalize, map } from "rxjs/operators";

import { AuthTokenType } from "./../models/auth-token-type";
import { AuthUser } from "./../models/auth-user";
import { Credentials } from "./../models/credentials";
import { ApiConfigService } from "./api-config.service";
import { APP_CONFIG, IAppConfig } from "./app.config";
import { RefreshTokenService } from "./refresh-token.service";
import { TokenStoreService } from "./token-store.service";
import { MsalService } from "@azure/msal-angular";

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private authStatusSource = new BehaviorSubject<boolean>(false);
  authStatus$ = this.authStatusSource.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    @Inject(APP_CONFIG) private appConfig: IAppConfig,
    private apiConfigService: ApiConfigService,
    private tokenStoreService: TokenStoreService,
    private refreshTokenService: RefreshTokenService,
    private msalAuthService: MsalService
  ) {
    this.updateStatusOnPageRefresh();
    this.refreshTokenService.scheduleRefreshToken(this.isAuthUserLoggedIn(), false);
  }

  login(credentials: Credentials): Observable<boolean> {
    const headers = new HttpHeaders({ "Content-Type": "application/json" });
    return this.http
      .post(`${this.appConfig.apiEndpoint}/${this.apiConfigService.configuration.loginPath}`,
        credentials, { headers: headers })
      .pipe(
        map((response: any) => {
          this.tokenStoreService.setRememberMe(credentials.rememberMe);
          if (!response) {
            console.error("There is no `{'" + this.apiConfigService.configuration.accessTokenObjectKey +
              "':'...','" + this.apiConfigService.configuration.refreshTokenObjectKey + "':'...value...'}` response after login.");
            this.authStatusSource.next(false);
            return false;
          }
          this.tokenStoreService.storeLoginSession(response);
          this.refreshTokenService.scheduleRefreshToken(true, true);
          this.authStatusSource.next(true);
          
          return true;
        }),
        catchError((error: HttpErrorResponse) => throwError(error))
      );
  }

  ssoAuthOnAPI(): Observable<boolean> {
    const headers = new HttpHeaders({ "Content-Type": "application/json" });
    var result= this.http
      .post(`${this.appConfig.apiEndpoint}/account/ssoAuth`,
        null, { headers: headers })
      .pipe(
        map((response: any) => {
          
          if (!response) {
            console.error("There is no `{'" + this.apiConfigService.configuration.accessTokenObjectKey +
              "':'...','" + this.apiConfigService.configuration.refreshTokenObjectKey + "':'...value...'}` response after login.");
            this.authStatusSource.next(false);
            return false;
          }
          this.tokenStoreService.storeLoginSession(response);
          this.refreshTokenService.scheduleRefreshToken(true, true);
          this.authStatusSource.next(true);
          
          return true;
        }),
        catchError(error => {
         
            console.error(error);
            
          return throwError(error);
        })
      );

    
      return result;
      
  }

  getBearerAuthHeader(): HttpHeaders {
    return new HttpHeaders({
      "Content-Type": "application/json",
      "Authorization": `Bearer ${this.tokenStoreService.getRawAuthToken(AuthTokenType.AccessToken)}`
    });
  }

  getIsActiveDirectory(): boolean {
    return this.tokenStoreService.getIsActiveDirectory();
  }

  logout(navigateToHome: boolean): void {
    const headers = new HttpHeaders({ "Content-Type": "application/json" });
    const refreshToken = encodeURIComponent(this.tokenStoreService.getRawAuthToken(AuthTokenType.RefreshToken));
    var isAdUser =  this.getIsActiveDirectory();
    this.http
      .get(`${this.appConfig.apiEndpoint}/${this.apiConfigService.configuration.logoutPath}?refreshToken=${refreshToken}`,
        { headers: headers })
      .pipe(
        map(response => response || {}),
        catchError((error: HttpErrorResponse) => throwError(error)),
        finalize(() => {
          this.tokenStoreService.deleteAuthTokens();
          this.refreshTokenService.unscheduleRefreshToken(true);
          this.authStatusSource.next(false);
          if(isAdUser)
          {
            this.msalAuthService.logout();
          }
          if (navigateToHome) {
            this.router.navigate(["/"]);
          }
        }))
      .subscribe(result => {
        console.log("logout", result);
      });

     
  }

  isAuthUserLoggedIn(): boolean {
    return this.tokenStoreService.hasStoredAccessAndRefreshTokens() &&
      !this.tokenStoreService.isAccessTokenTokenExpired();
  }

  getAuthUser(): AuthUser | null {
    if (!this.isAuthUserLoggedIn()) {
      return null;
    }

    const decodedToken = this.tokenStoreService.getDecodedAccessToken();
    const roles = this.tokenStoreService.getDecodedTokenRoles();
    const entity = this.tokenStoreService.getAuthUserEntity();
    return Object.freeze({
      userId: decodedToken["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"],
      userName: decodedToken["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"],
      email: decodedToken["Email"],
      displayName: decodedToken["DisplayName"],
      entity: entity,
      roles: roles
    });
  }

  isAuthUserInRoles(requiredRoles: string[]): boolean {
    const user = this.getAuthUser();
    if (!user || !user.roles) {
      return false;
    }
   
    return requiredRoles.some(requiredRole => {
      if(user.roles){
        return user.roles.indexOf(requiredRole.toLowerCase()) >= 0;
      } else {
        return false;
      }
    })
  }

  isAuthUserInEntities(requiredEntities: string[] | null): boolean {
    const user = this.getAuthUser();
    if (!user || !user.entity) {
      return false;
    }
    if (!requiredEntities) {
      return true;
    }
    return requiredEntities.some(requiredEntity => {
      if (user.entity) {
        return user.entity.indexOf(requiredEntity.toLowerCase()) >= 0;
      } else {
        return false;
      }
    });
  }

  isAuthUserInRole(requiredRole: string): boolean {
    return this.isAuthUserInRoles([requiredRole]);
  }

  authUserEntity(): string[] | null {
    const user = this.getAuthUser(); 
    if (!user) {
      return null;
    } else {
      return user.entity;
    }
  }

  private updateStatusOnPageRefresh(): void {
    this.authStatusSource.next(this.isAuthUserLoggedIn());
  }
}
